import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  CheckCircleOutline as CheckCircleIcon,
  HomeOutlined as HomeIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { getIdToken } from '../utils/authStorage';
import { normalizeSignatureRequest } from '../utils/eSignature';

/**
 * Post-sign confirmation — verifies status against live API when requestId present (ZRF-ESIGN-01).
 * Most visitors are logged-out signers from an email link, so internal IDs stay hidden and the
 * home button only shows for signed-in users (it would otherwise land on the login screen).
 */
const SignatureSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('requestId') || '';
  const isSignedIn = Boolean(getIdToken());

  const [loading, setLoading] = useState(Boolean(requestId));
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!requestId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/api/e-signature/status/${requestId}`);
        if (!cancelled) {
          setRequest(normalizeSignatureRequest(res.data || {}));
          setError('');
        }
      } catch (err) {
        console.error('❌ Signature success status check failed:', err);
        if (!cancelled) {
          setError(err.response?.data?.error || 'Could not verify signature status');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  const status = request?.status;
  const isComplete = status === 'completed';
  const isPending = status === 'pending' || status === 'sent';
  const hasRealTitle =
    Boolean(request?.documentTitle) &&
    request.documentTitle !== 'Untitled document' &&
    request.documentTitle !== `Document ${request.documentId}`;
  const bodySx = { mb: isSignedIn ? 4 : 0 };

  return (
    <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
      <Paper sx={{ p: 4 }}>
        {loading ? (
          <Box>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Confirming signature with the server…
            </Typography>
          </Box>
        ) : (
          <>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              {isComplete
                ? 'Document Signed Successfully!'
                : isPending
                  ? 'Signature recorded'
                  : 'Thank you'}
            </Typography>

            {hasRealTitle && (
              <Typography variant="body1" sx={{ mb: 2 }}>
                {request.documentTitle}
              </Typography>
            )}

            {(isComplete || isPending) && (
              <Chip
                label={isComplete ? 'Fully signed' : 'Waiting on other signers'}
                color={isComplete ? 'success' : 'info'}
                variant="outlined"
                sx={{ mb: 2, fontWeight: 500, color: isComplete ? 'success.dark' : 'info.dark' }}
              />
            )}

            {error && (
              <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
                We couldn't confirm your signature just now. It may still have been saved. If
                you're unsure, contact the person who sent you this document.
              </Alert>
            )}

            {!error && isComplete && (
              <Typography variant="body1" color="text.secondary" sx={bodySx}>
                Everyone has signed, so this document is now complete. You can close this page.
              </Typography>
            )}

            {!error && isPending && (
              <Typography variant="body1" color="text.secondary" sx={bodySx}>
                Thanks, your signature is saved. The document will be complete once everyone has
                signed. You can close this page.
              </Typography>
            )}

            {(!requestId || (!error && !isComplete && !isPending)) && (
              <Typography variant="body1" color="text.secondary" sx={bodySx}>
                Thank you for signing the document. You can close this page.
              </Typography>
            )}

            {isSignedIn && (
              <Button
                variant="contained"
                startIcon={<HomeIcon />}
                onClick={() => navigate('/')}
              >
                Return to Home
              </Button>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default SignatureSuccess;
